import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Warning, CircleNotch, ShieldWarning, CheckCircle, ArrowRight, Lock, User,
} from '@phosphor-icons/react';
import {
  listConflictReviews,
  assessRegulatoryBasis,
  decideConflictReview,
  BASIS_OPTIONS,
  CONSEQUENCE_LABEL,
  ROLE_LABEL,
  MATCH_METHOD_LABEL,
  type ConflictReview,
  type ConflictConsequence,
  type ConflictRegulatoryBasis,
} from '@/lib/conflictReviewsApi';
import { PageHeader } from '@/components/shared/PageHeader';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { EmptyState } from '@/components/shared/EmptyState';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ExpandableTextarea } from '@/components/ui/expandable-textarea';
import { getApiErrorMessage } from '@/lib/api';
import { useAuthStore } from '@/features/auth/store';

/**
 * Conflict Review — the clerk-facing surface for a conflict found on a LIVE matter.
 *
 * This page holds NO regulatory logic. Whether cessation is mandatory, and which of
 * continue / cease / reassign this user may choose, are computed by the API and rendered
 * here. Re-deriving rC21 or the silk/admin override in React would be a second copy of a
 * regulatory rule — and two copies drift.
 *
 * It also does not perform the consequences itself. Cease and reassign hand back an
 * allocation and send the clerk into the EXISTING return lifecycle, which is where the
 * rC27 explanation/consent gate lives. Shortcutting that here would bypass it.
 */
export function ConflictReviewsPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isClerk = user?.memberType === 'senior_clerk' || user?.memberType === 'junior_clerk';
  // Mounted under /admin, /clerk AND /barrister (a silk decides reviews on their own matters).
  // Sending a silk to /admin/matters/... bounced them off an admin-only route and lost the
  // return-of-instructions handoff.
  const basePath = isClerk ? '/clerk' : user?.memberType === 'admin' ? '/admin' : '/barrister';

  const [reviews, setReviews] = useState<ConflictReview[]>([]);
  const [loading, setLoading] = useState(true);
  // A failed load must not read as "No conflicts awaiting a decision" on a regulatory queue.
  const [loadError, setLoadError] = useState(false);
  /** Set when the server refuses this role outright (403) — distinct from a failure to load. */
  const [refused, setRefused] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tab, setTab] = useState<'pending' | 'decided'>('pending');

  const [basis, setBasis] = useState<ConflictRegulatoryBasis | ''>('');
  const [basisNotes, setBasisNotes] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      setReviews(await listConflictReviews(tab));
      setLoadError(false);
      setRefused(false);
    } catch (err) {
      // A 403 is a refusal, not an outage. Deciding mid-matter conflicts is restricted, and the
      // queue answered 403 to everyone else — which rendered as "couldn't load, try again", a
      // retry that can never succeed. Say what is actually true.
      if ((err as { response?: { status?: number } })?.response?.status === 403) {
        setRefused(true);
        setLoadError(false);
        setLoading(false);
        return;
      }
      // Shown inline (below), not as a toast: the queue polls every minute and a persistent
      // outage would otherwise toast forever while the list claimed there was nothing to decide.
      console.error('Could not load conflict reviews:', getApiErrorMessage(err));
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // A conflict raised while this page is open was previously invisible until a manual
  // reload — on a queue whose whole purpose is "something needs deciding", that is the
  // wrong default. Poll, and refresh whenever the tab regains focus (the common case:
  // the clerk was on the matter and comes back).
  useEffect(() => {
    const interval = setInterval(() => { void load(); }, 60_000);
    const onFocus = () => { void load(); };
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const selected = useMemo(
    () => reviews.find((r) => r.id === selectedId) ?? null,
    [reviews, selectedId],
  );

  useEffect(() => {
    setBasis(selected?.regulatoryBasis ?? '');
    setBasisNotes(selected?.basisNotes ?? '');
    setReason('');
  }, [selectedId, selected?.regulatoryBasis, selected?.basisNotes]);

  async function handleAssess() {
    if (!selected || !basis) return;
    setBusy(true);
    try {
      await assessRegulatoryBasis(selected.id, {
        regulatoryBasis: basis,
        ...(basisNotes.trim() ? { basisNotes: basisNotes.trim() } : {}),
      });
      toast.success('Regulatory basis recorded');
      await load();
    } catch (err) {
      // Surface the API's own wording — it explains the rule, which is the point.
      toast.error(getApiErrorMessage(err, 'Could not record the regulatory basis'));
    } finally {
      setBusy(false);
    }
  }

  async function handleDecide(consequence: ConflictConsequence) {
    if (!selected) return;
    if (!reason.trim()) {
      toast.error('A written reason is required for this decision.');
      return;
    }
    setBusy(true);
    try {
      const result = await decideConflictReview(selected.id, { consequence, reason: reason.trim() });
      toast.success(`Decision recorded: ${CONSEQUENCE_LABEL[consequence]}`);

      // Cease / reassign do not remove counsel here. The API returns the allocation to
      // return; the clerk completes it through the existing return flow on the matter.
      if (result.allocationToReturn) {
        toast.info('Counsel is still on record. Complete the return of instructions to take them off.', {
          duration: 8000,
        });
        navigate(`${basePath}/matters/${selected.caseId}?returnAllocation=${result.allocationToReturn}`);
        return;
      }
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not record the decision'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        onBack={() => navigate(-1)}
        title="Conflict reviews"
        subtitle="Conflicts found on live matters. Each needs the regulatory position recorded, then a decision."
      />

      <div className="flex gap-1 border-b border-border">
        {(['pending', 'decided'] as const).map((t) => (
          <button
            key={t}
            onClick={() => { setTab(t); setSelectedId(null); }}
            className={`px-4 py-2 text-sm font-medium transition ${
              tab === t
                ? 'border-b-2 border-brand-600 text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t === 'pending' ? 'Awaiting decision' : 'Decided'}
          </button>
        ))}
      </div>

      {loadError && reviews.length > 0 && (
        <div role="alert" className="flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
          <span>Couldn't refresh conflict reviews — this list may be out of date.</span>
          <Button variant="outline" size="sm" onClick={() => void load()}>Try again</Button>
        </div>
      )}

      {loading && reviews.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <CircleNotch className="mr-2 h-5 w-5 animate-spin" /> Loading…
        </div>
      ) : refused ? (
        <Card>
          <EmptyState
            icon={<ShieldWarning className="h-6 w-6" />}
            title="Conflict reviews aren't available to your role"
            description="Deciding whether chambers may act despite a conflict is restricted to silks and the chambers administrator. This queue is not empty — it is not yours to decide."
          />
        </Card>
      ) : loadError && reviews.length === 0 ? (
        <Card><QueryErrorState what="conflict reviews" onRetry={() => void load()} /></Card>
      ) : reviews.length === 0 ? (
        <Card className="p-10 text-center">
          <CheckCircle className="mx-auto mb-3 h-8 w-8 text-success" weight="fill" />
          <div className="text-sm font-medium text-foreground">
            {tab === 'pending' ? 'No conflicts awaiting a decision' : 'Nothing decided yet'}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Conflicts found on a live matter appear here for review.
          </p>
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)]">
          {/* Queue */}
          <ul className="space-y-2">
            {reviews.map((r) => {
              const isSelected = r.id === selectedId;
              return (
                <li key={r.id}>
                  <button
                    onClick={() => setSelectedId(r.id)}
                    className={`w-full rounded-md border px-3 py-2.5 text-left transition ${
                      isSelected
                        ? 'border-brand-300 bg-brand-50 dark:bg-brand-900/30'
                        : 'border-border hover:bg-muted/40'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-foreground">
                          {r.case.caseName}
                        </div>
                        <div className="text-xs text-muted-foreground">{r.case.caseNumber}</div>
                      </div>
                      <span
                        className={`inline-flex flex-shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
                          r.severity === 'critical'
                            ? 'bg-danger-bg text-danger ring-danger/20'
                            : 'bg-warning-bg text-warning ring-warning/20'
                        }`}
                      >
                        {r.severity}
                      </span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                      {r.counsel.length > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <User className="h-3 w-3" /> {r.counsel.map((c) => c.name).join(', ')}
                        </span>
                      )}
                      {r.status === 'pending' && !r.regulatoryBasis && (
                        <span className="text-amber-700 dark:text-amber-400">· basis not recorded</span>
                      )}
                      {r.consequence && <span>· {CONSEQUENCE_LABEL[r.consequence]}</span>}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Detail */}
          {selected ? (
            <Card className="p-5">
              <ReviewDetail review={selected} basePath={basePath} />

              {selected.status === 'pending' ? (
                <>
                  {/* Step 1 — the rule */}
                  <section className="mt-5 border-t border-border pt-4">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      1 · Regulatory position
                    </h3>
                    <p className="mt-1 text-xs text-muted-foreground">
                      What the Handbook requires of this conflict. Recorded before a decision — the
                      basis determines which consequences are open.
                    </p>
                    <select
                      value={basis}
                      onChange={(e) => setBasis(e.target.value as ConflictRegulatoryBasis)}
                      aria-label="Regulatory basis"
                      className="mt-2 h-9 w-full rounded-md border border-border bg-card px-2 text-sm text-foreground focus:border-brand-600 focus:outline-none"
                    >
                      <option value="">Select the applicable basis…</option>
                      {BASIS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    <ExpandableTextarea
                      value={basisNotes}
                      onChange={setBasisNotes}
                      placeholder="Notes on the position (optional)"
                      className="mt-2"
                    />
                    <Button
                      onClick={handleAssess}
                      disabled={!basis || busy}
                      className="mt-2"
                      variant="secondary"
                    >
                      {selected.regulatoryBasis ? 'Update basis' : 'Record basis'}
                    </Button>
                  </section>

                  {/* Step 2 — the consequence */}
                  <section className="mt-5 border-t border-border pt-4">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      2 · Decision
                    </h3>
                    <ExpandableTextarea
                      value={reason}
                      onChange={setReason}
                      placeholder="Reason for this decision (required)"
                      className="mt-2"
                    />
                    <div className="mt-3 flex flex-wrap gap-2">
                      {(['continue_acting', 'cease', 'reassign'] as ConflictConsequence[]).map((c) => {
                        const allowed = selected.availableConsequences.includes(c);
                        const blocked = selected.blockedConsequences.find((b) => b.consequence === c);
                        return (
                          <div key={c} className="flex flex-col">
                            <Button
                              onClick={() => handleDecide(c)}
                              disabled={!allowed || busy}
                              variant={c === 'continue_acting' ? 'secondary' : 'primary'}
                              title={blocked?.reason}
                            >
                              {!allowed && <Lock className="mr-1.5 h-3.5 w-3.5" />}
                              {CONSEQUENCE_LABEL[c]}
                            </Button>
                            {blocked && (
                              <span className="mt-1 max-w-[15rem] text-xs text-muted-foreground">
                                {blocked.reason}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </section>
                </>
              ) : (
                <DecidedSummary review={selected} />
              )}
            </Card>
          ) : (
            <Card className="flex items-center justify-center p-10 text-sm text-muted-foreground">
              Select a review to see the conflict and record a decision.
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

function ReviewDetail({ review, basePath }: { review: ConflictReview; basePath: string }) {
  return (
    <>
      <div className="flex items-start gap-3">
        <ShieldWarning
          className={`mt-0.5 h-5 w-5 flex-shrink-0 ${
            review.severity === 'critical' ? 'text-danger' : 'text-warning'
          }`}
          weight="fill"
        />
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-foreground">{review.case.caseName}</h2>
          <a
            href={`${basePath}/matters/${review.caseId}`}
            className="text-xs text-brand-600 dark:text-brand-400 hover:underline"
          >
            {review.case.caseNumber} <ArrowRight className="inline h-3 w-3" />
          </a>
        </div>
      </div>

      {/* WHAT the conflict is. A reviewer cannot record a regulatory basis for something
          the screen never shows them — this block is the evidence the decision rests on. */}
      <section className="mt-4 rounded-md border border-border bg-muted/30 p-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          What was found
        </h3>
        {review.conflicts.length === 0 ? (
          <p className="mt-1 text-xs text-muted-foreground">
            No conflict records are linked to this review. Open the matter to see its conflict
            history.
          </p>
        ) : (
          <ul className="mt-2 space-y-2">
            {review.conflicts.map((c) => (
              <li key={c.id} className="flex items-start gap-2">
                <ShieldWarning
                  className={`mt-0.5 h-3.5 w-3.5 flex-shrink-0 ${
                    c.severity === 'critical' ? 'text-danger' : 'text-warning'
                  }`}
                  weight="fill"
                />
                <div className="min-w-0 text-sm">
                  <div className="text-foreground">{c.reason}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {MATCH_METHOD_LABEL[c.matchMethod] ?? c.matchMethod}
                    {c.existingMatterNumber ? ` · ${c.existingMatterNumber}` : ''}
                    {c.existingBarrister ? ` · ${c.existingBarrister}` : ''}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <dl className="mt-4 grid gap-x-6 gap-y-3 sm:grid-cols-2">
        <Field label="Counsel affected">
          {review.counsel.length === 0 ? (
            <span className="text-muted-foreground">No counsel currently instructed</span>
          ) : (
            <ul className="space-y-0.5">
              {review.counsel.map((c) => (
                <li key={c.allocationId}>
                  {c.name}{c.isSilk ? ' KC' : ''}{' '}
                  <span className="text-muted-foreground">· {ROLE_LABEL[c.role]}</span>
                </li>
              ))}
            </ul>
          )}
        </Field>
        <Field label="Engagement">
          <span className={review.instruction.status === 'suspended' ? 'text-amber-700 dark:text-amber-400' : ''}>
            {review.instruction.status}
          </span>
          {review.instruction.status === 'suspended' && (
            <span className="block text-xs text-muted-foreground">
              No new counsel can be allocated until this is decided. Counsel remains on record.
            </span>
          )}
        </Field>
        <Field label="Regulatory basis">
          {review.basisLabel ?? <span className="text-amber-700 dark:text-amber-400">Not yet recorded</span>}
        </Field>
        <Field label="Required consequence">
          {review.mandatoryCessation === null ? (
            <span className="text-muted-foreground">Determined by the basis</span>
          ) : review.mandatoryCessation ? (
            <span className="inline-flex items-center gap-1 text-danger">
              <Warning className="h-3.5 w-3.5" weight="fill" /> Cessation is mandatory
            </span>
          ) : (
            'Cessation not mandatory on this basis'
          )}
        </Field>
        {review.basisNotes && <Field label="Basis notes" wide>{review.basisNotes}</Field>}
      </dl>
    </>
  );
}

function DecidedSummary({ review }: { review: ConflictReview }) {
  return (
    <section className="mt-5 border-t border-border pt-4">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Decision</h3>
      <dl className="mt-2 grid gap-x-6 gap-y-3 sm:grid-cols-2">
        <Field label="Outcome">
          {review.consequence ? CONSEQUENCE_LABEL[review.consequence] : '—'}
        </Field>
        <Field label="Decided by">
          {review.decidedBy ? `${review.decidedBy.firstName} ${review.decidedBy.lastName}` : '—'}
          {review.decidedAt && (
            <span className="block text-xs text-muted-foreground">
              {new Date(review.decidedAt).toLocaleString('en-GB')}
            </span>
          )}
        </Field>
        {review.assessedBy && (
          <Field label="Basis assessed by">
            {review.assessedBy.firstName} {review.assessedBy.lastName}
          </Field>
        )}
        {review.decisionReason && <Field label="Reason" wide>{review.decisionReason}</Field>}
      </dl>
    </section>
  );
}

function Field({ label, children, wide }: { label: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className={wide ? 'sm:col-span-2' : ''}>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm text-foreground">{children}</dd>
    </div>
  );
}

export default ConflictReviewsPage;
